import React, { useState, useEffect } from "react"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"
import PlatformSelector from "aldoo-ra/PlatformSelector"
import useProductEditorTexts from "../useProductEditorTexts"

export default function SupportedPlatformsEditor({
  onChange,
  value,
  field,
  readOnly = false,
}) {
  const t = useProductEditorTexts()
  const resolveOptions = () => {
    let { options } = field
    if (!options) return []

    if (typeof options === "function") {
      return options()
    }

    if (Array.isArray(options)) {
      return options.map((option) => {
        if (typeof option === "string") {
          return {
            value: option,
            label: option,
          }
        }
        return option
      })
    }

    return options || []
  }

  const getInitialValue = () => {
    let defaultValue = field.default || ["any"]
    let initialValue = value !== undefined && value !== null && value !== "" ? value : defaultValue
    
    // Ensure we always have an array
    if (!Array.isArray(initialValue)) {
      initialValue = [initialValue]
    }
    
    // Convert array items to strings and then to option objects
    const options = resolveOptions()
    return initialValue.map(item => {
      // Extract string value from various formats
      let platformValue
      if (typeof item === "object" && item.value) {
        platformValue = String(item.value)
      } else {
        platformValue = String(item)
      }
      
      const matchingOption = options.find(opt => opt.value === platformValue)
      return matchingOption || { value: platformValue, label: platformValue }
    })
  }

  const [localValue, setLocalValue] = useState(getInitialValue)

  // Call onChange with initial value after mount
  useEffect(() => {
    const initialValue = getInitialValue()
    const platforms = initialValue.map(opt => opt.value)
    onChange?.(platforms)
  }, [])

  // Handle external value changes
  useEffect(() => {
    if (value !== undefined && value !== null) {
      const newValue = getInitialValue()
      setLocalValue(newValue)
    }
  }, [value, field.default])

  const handleValueChange = (selectedOptions) => {
    let selectedPlatforms = selectedOptions.map(opt => opt.value)
    const currentPlatforms = localValue.map(opt => opt.value)
    
    // Check if user was switching from "any" to specific platforms
    if ((currentPlatforms.length === 0 || currentPlatforms.includes("any")) && 
        selectedPlatforms.includes("any") && 
        selectedPlatforms.length > 1) {
      // User had empty/any state and clicked on a specific platform
      // Remove "any" and keep only the specific platforms
      selectedPlatforms = selectedPlatforms.filter(p => p !== "any")
    } else if (selectedPlatforms.includes("any") && selectedPlatforms.length > 1) {
      // User selected "any" along with other platforms, keep only "any"
      selectedPlatforms = ["any"]
    }
    
    // Convert back to option objects for local state
    const options = resolveOptions()
    const newLocalValue = selectedPlatforms.map(platform => {
      const matchingOption = options.find(opt => opt.value === platform)
      return matchingOption || { value: platform, label: platform }
    })
    
    setLocalValue(newLocalValue)
    onChange?.(selectedPlatforms)
  }

  const options = resolveOptions()

  return (
    <div className="supported-platforms-editor">
      <div className="flex flex-col gap-3">
        <PlatformSelector
          variant="admin_highlighted"
          disabled={readOnly}
          // the labels in the site's language (a platform's name stays as it is)
          platforms={options.map((option) => ({ ...option, label: t(option.label) }))}
          placeholder={t`Select supported platforms...`}
          className="flex-grow text-admin_text bg-admin_paper"
          value={localValue.map((option) => ({ ...option, label: t(option.label) }))}
          onChange={handleValueChange}
        />
        
        {/* Selected Platforms Display */}
        <div className="flex items-center gap-2 flex-wrap">
          {(() => {
            const platforms = localValue.map(opt => {
              // Ensure we have a string value
              const value = opt.value
              return typeof value === "string" ? value : String(value)
            })
            if (platforms.length === 0 || platforms.includes("any")) {
              return (
                <div className="flex items-center gap-1 bg-admin_canvas px-2 py-1 rounded text-sm">
                  {renderPlatformIcon("any", "w-3 h-3 text-admin_text_secondary")}
                  <span className="text-admin_text_secondary">{t`All Platforms`}</span>
                </div>
              )
            }
            return platforms.map(platform => {
              // Ensure platform is a string before using it
              const platformStr = typeof platform === "string" ? platform : String(platform)
              return (
                <div key={platformStr} className="flex items-center gap-1 bg-admin_canvas px-2 py-1 rounded text-sm">
                  {renderPlatformIcon(platformStr, "w-3 h-3 text-admin_text_secondary")}
                  <span className="text-admin_text_secondary">
                    {platformStr === "ios" ? "iOS" :
                     platformStr === "android" ? "Android" :
                     platformStr === "windows" ? "Windows" :
                     platformStr === "linux" ? "Linux" :
                     platformStr === "macos" ? "macOS" :
                     platformStr === "web" ? "Web" :
                     platformStr.charAt(0).toUpperCase() + platformStr.slice(1)}
                  </span>
                </div>
              )
            })
          })()}
        </div>
      </div>

    </div>
  )
}